import { A, useParams } from "@solidjs/router";
import { createMemo, createSignal, Show } from "solid-js";
import type {
	Author,
	ChangeEvent,
	Customer,
	IssuedToken,
	Persona,
	Product,
	Questionnaire,
	RelevancyQuestionnaire,
	Report,
	User,
	View,
} from "../../api/domain.ts";
import { questionnaireStatus, relevancyStatus } from "../../api/domain.ts";
import { useApi } from "../../api/provider.tsx";
import {
	type ApiError,
	describeError,
	isNotFound,
	ok,
} from "../../api/result.ts";
import { Async } from "../../components/Async.tsx";
import { DeleteButton, deleteColumn } from "../../components/DeleteButton.tsx";
import {
	Actions,
	Button,
	CheckList,
	Form,
	SelectField,
	TextField,
	Toggle,
} from "../../components/Form.tsx";
import { Modal } from "../../components/Modal.tsx";
import { Panel } from "../../components/Panel.tsx";
import { Table } from "../../components/Table.tsx";
import { Tabs } from "../../components/Tabs.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { createLoader, loadedValue } from "../../lib/async.ts";
import { formatMoment } from "../../lib/format.ts";
import { useSession } from "../../session/provider.tsx";
import styles from "./admin.module.scss";
import { describeChange, newestFirst } from "./history.ts";
import { statusLabel } from "./questionnaire-status.ts";

const blankCustomer = (): Customer => ({
	id: "",
	name: "",
	description: null,
	products: [],
	active: true,
	created_at: Date.now(),
});

const blankUser = (customer: string | undefined): Partial<User> => ({
	name: "",
	email: null,
	role: customer === undefined ? "admin" : "customer",
	customer: customer ?? null,
	author: null,
	active: true,
});

/** Whether a questionnaire of either kind is still waiting on someone. */
const isOpen = (status: string): boolean => status !== "evaluated";

/**
 * The token, shown exactly once. The dialog closes only through the button,
 * because a stray Escape here would lose a secret nobody can look up again.
 */
const TokenDialog = (props: {
	issued: IssuedToken | undefined;
	onDone: () => void;
}) => {
	const i18n = useI18n();
	const [copied, setCopied] = createSignal(false);

	const copy = async (token: string) => {
		try {
			await navigator.clipboard.writeText(token);
			setCopied(true);
		} catch {
			setCopied(false);
		}
	};

	return (
		<Modal
			open={props.issued !== undefined}
			title={i18n.t().users.issuedTitle}
			description={props.issued?.user.name}
			onClose={() => undefined}
		>
			<Show when={props.issued}>
				{(issued) => (
					<div class={styles.stack}>
						<p class={styles.note}>{i18n.t().users.issuedIntro}</p>
						<code class={styles.mono} data-testid="issued-token">
							{issued().token}
						</code>
						<Actions>
							<Button
								label={copied() ? i18n.t().common.copied : i18n.t().common.copy}
								onClick={() => void copy(issued().token)}
							/>
							<Button
								label={i18n.t().users.understood}
								tone="primary"
								onClick={() => {
									setCopied(false);
									props.onDone();
								}}
							/>
						</Actions>
					</div>
				)}
			</Show>
		</Modal>
	);
};

export type UsersPanelProps = {
	/** The customer whose members are listed; absent lists the consultants. */
	customer?: string;
	title: string;
	description?: string;
};

/** One customer's members, or the team: the table, the create form and the tokens. */
export const UsersPanel = (props: UsersPanelProps) => {
	const api = useApi();
	const i18n = useI18n();

	const users = createLoader(() => api.list("users"));
	const authors = createLoader(() => api.list("authors"));
	const [draft, setDraft] = createSignal<Partial<User> | undefined>(undefined);
	const [issued, setIssued] = createSignal<IssuedToken | undefined>(undefined);
	const [problem, setProblem] = createSignal<string | undefined>(undefined);

	const mine = createMemo(() =>
		(loadedValue(users.value()) ?? []).filter((user) =>
			props.customer === undefined
				? user.role === "admin"
				: user.customer === props.customer,
		),
	);

	const create = async (user: Partial<User>) => {
		setProblem(undefined);
		const result = await api.createUser(user);
		if (!result.ok) return setProblem(describeError(i18n.t(), result.error));
		setDraft(undefined);
		setIssued(result.value);
		return users.reload();
	};

	const rotate = async (user: User) => {
		if (!window.confirm(i18n.t().users.rotateHint)) return;
		const result = await api.rotateToken(user.id);
		if (!result.ok) return setProblem(describeError(i18n.t(), result.error));
		setIssued(result.value);
		return users.reload();
	};

	const revoke = async (user: User) => {
		const result = await api.revokeToken(user.id);
		if (!result.ok) return setProblem(describeError(i18n.t(), result.error));
		return users.reload();
	};

	const authorName = (id: string | null): string =>
		(loadedValue(authors.value()) ?? []).find(
			(author: Author) => author.id === id,
		)?.name ?? "";

	return (
		<Panel
			title={props.title}
			description={props.description}
			actions={
				<Button
					label={i18n.t().users.newUser}
					onClick={() => setDraft(blankUser(props.customer))}
				/>
			}
		>
			<Show when={draft()}>
				{(user) => (
					<Form>
						<TextField
							label={i18n.t().common.name}
							value={user().name ?? ""}
							onInput={(name) => setDraft({ ...user(), name })}
						/>
						<TextField
							label={i18n.t().authors.email}
							optional
							value={user().email ?? ""}
							onInput={(email) =>
								setDraft({ ...user(), email: email === "" ? null : email })
							}
						/>
						<Show when={props.customer !== undefined}>
							<SelectField
								label={i18n.t().users.author}
								hint={i18n.t().users.authorHint}
								optional
								placeholder={i18n.t().common.none}
								choices={(loadedValue(authors.value()) ?? []).map((author) => ({
									id: author.id,
									label: author.name,
								}))}
								value={user().author ?? ""}
								onPick={(author) =>
									setDraft({ ...user(), author: author === "" ? null : author })
								}
							/>
						</Show>
						<Show when={problem() !== undefined}>
							<p class={styles.problem}>{problem()}</p>
						</Show>
						<Actions>
							<Button
								label={i18n.t().common.create}
								tone="primary"
								disabled={(user().name ?? "").trim() === ""}
								onClick={() => void create(user())}
							/>
							<Button
								label={i18n.t().common.cancel}
								onClick={() => setDraft(undefined)}
							/>
						</Actions>
					</Form>
				)}
			</Show>

			<Show when={draft() === undefined && problem() !== undefined}>
				<p class={styles.problem}>{problem()}</p>
			</Show>

			<Async value={users.value()} onRetry={users.reload}>
				{() => (
					<Table
						rows={mine()}
						rowKey={(user) => user.id}
						emptyLabel={i18n.t().users.none}
						columns={[
							{
								key: "name",
								header: i18n.t().common.name,
								cell: (user) => user.name,
							},
							{
								key: "email",
								header: i18n.t().authors.email,
								cell: (user) => user.email ?? "",
							},
							{
								key: "role",
								header: i18n.t().users.role,
								cell: (user) =>
									user.role === "admin"
										? i18n.t().users.roleAdmin
										: i18n.t().users.roleCustomer,
							},
							{
								key: "author",
								header: i18n.t().users.author,
								cell: (user) => authorName(user.author),
							},
							{
								key: "token",
								header: i18n.t().users.tokenPrefix,
								cell: (user) => (
									<span class={styles.mono}>
										{user.token_prefix ?? i18n.t().users.revoked}
									</span>
								),
							},
							{
								key: "seen",
								header: i18n.t().users.lastSeen,
								cell: (user) =>
									user.last_seen_at === null
										? i18n.t().users.never
										: formatMoment(user.last_seen_at, i18n.locale()),
							},
							{
								key: "active",
								header: i18n.t().common.active,
								cell: (user) =>
									user.active ? i18n.t().common.yes : i18n.t().common.no,
							},
							{
								key: "actions",
								header: i18n.t().common.actions,
								align: "end",
								cell: (user) => (
									<span class={styles.rowActions}>
										<Button
											label={i18n.t().users.rotate}
											onClick={() => void rotate(user)}
										/>
										<Button
											label={i18n.t().users.revoke}
											tone="danger"
											disabled={!user.active}
											onClick={() => void revoke(user)}
										/>
										<DeleteButton
											collection="users"
											id={user.id}
											label={user.name}
											onRemoved={users.reload}
										/>
									</span>
								),
							},
						]}
					/>
				)}
			</Async>

			<TokenDialog issued={issued()} onDone={() => setIssued(undefined)} />
		</Panel>
	);
};

/** Every customer, what it holds and who may sign in; plus the team. */
export const CustomersPage = () => {
	const api = useApi();
	const i18n = useI18n();

	const customers = createLoader(() => api.list("customers"));
	const users = createLoader(() => api.list("users"));
	const questionnaires = createLoader(() => api.list("questionnaires"));
	const relevancyQuestionnaires = createLoader(() =>
		api.list("relevancy-questionnaires"),
	);
	const reports = createLoader(() => api.list("reports"));
	const [draft, setDraft] = createSignal<Customer | undefined>(undefined);

	const save = async (customer: Customer) => {
		const result = await api.create("customers", customer);
		if (!result.ok) return;
		setDraft(undefined);
		customers.reload();
	};

	const userCount = (id: string): number =>
		(loadedValue(users.value()) ?? []).filter((user) => user.customer === id)
			.length;

	/**
	 * What is still waiting for this customer: their own relevancy questionnaires,
	 * and the product questionnaires of the products they list, which belong to
	 * nobody but shape what they will see.
	 */
	const openCount = (id: string): number => {
		const listed =
			(loadedValue(customers.value()) ?? []).find(
				(customer) => customer.id === id,
			)?.products ?? [];
		return (
			(loadedValue(questionnaires.value()) ?? []).filter(
				(entry) =>
					entry.product !== null &&
					listed.includes(entry.product) &&
					isOpen(questionnaireStatus(entry)),
			).length +
			(loadedValue(relevancyQuestionnaires.value()) ?? []).filter(
				(entry) => entry.customer === id && isOpen(relevancyStatus(entry)),
			).length
		);
	};

	const newestLocked = (id: string): number | undefined =>
		(loadedValue(reports.value()) ?? [])
			.filter((report) => report.customer === id && report.locked_at !== null)
			.map((report) => report.locked_at as number)
			.sort((left, right) => right - left)[0];

	return (
		<>
			<div class={styles.heading}>
				<h1>{i18n.t().customers.title}</h1>
			</div>

			<Panel
				actions={
					<Button
						label={i18n.t().customers.newCustomer}
						onClick={() => setDraft(blankCustomer())}
					/>
				}
			>
				<Show when={draft()}>
					{(customer) => (
						<Form>
							<TextField
								label={i18n.t().common.name}
								value={customer().name}
								onInput={(name) => setDraft({ ...customer(), name })}
							/>
							<TextField
								label={i18n.t().common.description}
								optional
								multiline
								value={customer().description ?? ""}
								onInput={(description) =>
									setDraft({
										...customer(),
										description: description === "" ? null : description,
									})
								}
							/>
							<Actions>
								<Button
									label={i18n.t().common.create}
									tone="primary"
									disabled={customer().name.trim() === ""}
									onClick={() => void save(customer())}
								/>
								<Button
									label={i18n.t().common.cancel}
									onClick={() => setDraft(undefined)}
								/>
							</Actions>
						</Form>
					)}
				</Show>

				<Async value={customers.value()} onRetry={customers.reload}>
					{(rows) => (
						<Table
							rows={rows}
							rowKey={(customer) => customer.id}
							emptyLabel={i18n.t().common.empty}
							columns={[
								{
									key: "name",
									header: i18n.t().common.name,
									cell: (customer) => (
										<span class={styles.rowActions}>
											<A href={`/customers/${customer.id}`}>{customer.name}</A>
											<Show when={!customer.active}>
												<span
													class={`${styles.ownerTag} ${styles.ownerInternal}`}
												>
													{i18n.t().customers.inactive}
												</span>
											</Show>
										</span>
									),
								},
								{
									key: "users",
									header: i18n.t().customers.userCount,
									align: "end",
									cell: (customer) => String(userCount(customer.id)),
								},
								{
									key: "report",
									header: i18n.t().customers.newestReport,
									cell: (customer) => {
										const at = newestLocked(customer.id);
										return at === undefined
											? i18n.t().common.none
											: formatMoment(at, i18n.locale());
									},
								},
								deleteColumn({
									header: i18n.t().common.actions,
									collection: "customers",
									id: (customer) => customer.id,
									label: (customer) => customer.name,
									onRemoved: customers.reload,
								}),
							]}
						/>
					)}
				</Async>
			</Panel>

			<UsersPanel
				title={i18n.t().customers.team}
				description={i18n.t().customers.teamIntro}
			/>
		</>
	);
};

type Owned = { id: string; customer: string | null };

type AccessGroupProps<Row extends Owned> = {
	title: string;
	customer: string;
	collection: "personas" | "views" | "reports" | "relevancy-questionnaires";
	rows: Row[];
	label: (row: Row) => string;
	/** The second column: a status or date; without it the column shows the identity. */
	detail?: (row: Row) => string;
	/** The header of the second column; defaults to the identity. */
	detailHeader?: string;
	onChanged: () => void;
};

/** One kind of owned entity: what the customer holds, and a picker for what is still free. */
const AccessGroup = <Row extends Owned>(props: AccessGroupProps<Row>) => {
	const api = useApi();
	const i18n = useI18n();
	const [picking, setPicking] = createSignal(false);
	const [problem, setProblem] = createSignal<string | undefined>(undefined);

	const owned = () =>
		props.rows.filter((row) => row.customer === props.customer);
	const free = () => props.rows.filter((row) => row.customer === null);

	const assign = async (row: Row, customer: string | null) => {
		setProblem(undefined);
		const result = await api.patch(props.collection, row.id, {
			customer,
		} as never);
		if (!result.ok) return setProblem(describeError(i18n.t(), result.error));
		return props.onChanged();
	};

	return (
		<Panel
			title={props.title}
			actions={
				<Button
					label={i18n.t().customers.assign}
					onClick={() => setPicking(true)}
				/>
			}
		>
			<Show when={problem() !== undefined && !picking()}>
				<p class={styles.problem}>{problem()}</p>
			</Show>
			<Table
				rows={owned()}
				rowKey={(row) => row.id}
				emptyLabel={i18n.t().customers.nothingAssigned}
				columns={[
					{
						key: "label",
						header: i18n.t().common.label,
						cell: (row) => props.label(row),
					},
					{
						key: "detail",
						header: props.detailHeader ?? i18n.t().common.identity,
						cell: (row) => props.detail?.(row) ?? row.id,
					},
					{
						key: "actions",
						header: i18n.t().common.actions,
						align: "end",
						cell: (row) => (
							<Button
								label={i18n.t().customers.unassign}
								onClick={() => void assign(row, null)}
							/>
						),
					},
				]}
			/>
			<Modal
				open={picking()}
				title={`${i18n.t().customers.assign}: ${props.title}`}
				onClose={() => {
					setPicking(false);
					setProblem(undefined);
				}}
			>
				<div class={styles.stack}>
					<Show when={problem() !== undefined}>
						<p class={styles.problem}>{problem()}</p>
					</Show>
					<Table
						rows={free()}
						rowKey={(row) => row.id}
						emptyLabel={i18n.t().customers.noneFree}
						columns={[
							{
								key: "label",
								header: i18n.t().common.label,
								cell: (row) => props.label(row),
							},
							{
								key: "actions",
								header: i18n.t().common.actions,
								align: "end",
								cell: (row) => (
									<Button
										label={i18n.t().customers.assign}
										tone="primary"
										onClick={() => void assign(row, props.customer)}
									/>
								),
							},
						]}
					/>
				</div>
			</Modal>
		</Panel>
	);
};

/** One customer: who may sign in, what they see, and what changed. */
export const CustomerPage = () => {
	const api = useApi();
	const i18n = useI18n();
	const session = useSession();
	const params = useParams<{ id: string }>();

	const customer = createLoader(() => api.get("customers", params.id));
	const products = createLoader(() =>
		api.list("products", { lang: i18n.locale() }),
	);
	const answers = createLoader(() => api.list("answers"));
	const personas = createLoader(() =>
		api.list("personas", { lang: i18n.locale() }),
	);
	const views = createLoader(() => api.list("views", { lang: i18n.locale() }));
	const reports = createLoader(() => api.list("reports"));
	const questionnaires = createLoader(() => api.list("questionnaires"));
	const relevancyQuestionnaires = createLoader(() =>
		api.list("relevancy-questionnaires"),
	);
	const users = createLoader(() => api.list("users"));
	const history = createLoader<ChangeEvent[]>(async () => {
		const own = await api.history("customer", params.id, { limit: 200 });
		if (!own.ok) return own;
		const members = (loadedValue(users.value()) ?? []).filter(
			(user) => user.customer === params.id,
		);
		const merged = [...own.value];
		for (const member of members) {
			const events = await api.history("user", member.id, { limit: 200 });
			if (events.ok) merged.push(...events.value);
		}
		return ok(newestFirst(merged));
	});
	const [tab, setTab] = createSignal("users");
	const [problem, setProblem] = createSignal<string | undefined>(undefined);

	const patch = async (fields: Partial<Customer>) => {
		setProblem(undefined);
		const result = await api.patch("customers", params.id, fields);
		if (!result.ok) return setProblem(describeError(i18n.t(), result.error));
		return customer.put(result.value);
	};

	/**
	 * The customer as last saved. `Async` hands its child a snapshot, so anything
	 * that edits from the current state must read the loader, not that snapshot.
	 * Otherwise each checkbox toggle would start from the list loaded first.
	 */
	const live = (fallback: Customer): Customer =>
		loadedValue(customer.value()) ?? fallback;

	const answered = createMemo(
		() =>
			new Set(
				(loadedValue(answers.value()) ?? []).map((answer) => answer.product),
			),
	);

	const productChoices = () =>
		(loadedValue(products.value()) ?? []).map((product: Product) => ({
			id: product.id,
			label: answered().has(product.id)
				? product.name
				: `${product.name} (${i18n.t().customers.withoutData})`,
		}));

	const missing = (error: ApiError) => isNotFound(error);

	const viewLabel = (id: string): string =>
		(loadedValue(views.value()) ?? []).find((view) => view.id === id)?.label ??
		id;
	const productName = (id: string | null): string =>
		(loadedValue(products.value()) ?? []).find((product) => product.id === id)
			?.name ??
		id ??
		i18n.t().questionnaires.noProduct;
	const personaName = (id: string): string =>
		(loadedValue(personas.value()) ?? []).find((persona) => persona.id === id)
			?.name ?? id;

	const AccessTab = (own: { customer: Customer }) => (
		<div class={styles.rows}>
			<Panel
				title={i18n.t().customers.products}
				description={i18n.t().customers.productsHint}
			>
				<Async value={products.value()} onRetry={products.reload}>
					{() => (
						<CheckList
							label={i18n.t().nav.products}
							choices={productChoices()}
							picked={live(own.customer).products}
							emptyLabel={i18n.t().common.empty}
							onChange={(picked) => void patch({ products: picked })}
						/>
					)}
				</Async>
			</Panel>
			<AccessGroup
				title={i18n.t().nav.personas}
				customer={own.customer.id}
				collection="personas"
				rows={loadedValue(personas.value()) ?? []}
				label={(persona: Persona) => persona.name}
				onChanged={() => void personas.reload()}
			/>
			<AccessGroup
				title={i18n.t().nav.views}
				customer={own.customer.id}
				collection="views"
				rows={loadedValue(views.value()) ?? []}
				label={(view: View) => view.label}
				onChanged={() => void views.reload()}
			/>
			<AccessGroup
				title={i18n.t().nav.reports}
				customer={own.customer.id}
				collection="reports"
				rows={loadedValue(reports.value()) ?? []}
				label={(report: Report) => viewLabel(report.view)}
				detailHeader={i18n.t().common.status}
				detail={(report: Report) =>
					report.locked_at === null
						? i18n.t().reports.live
						: `${i18n.t().reports.locked}: ${formatMoment(report.locked_at, i18n.locale())}`
				}
				onChanged={() => void reports.reload()}
			/>
			<AccessGroup
				title={i18n.t().customers.relevancyQuestionnaires}
				customer={own.customer.id}
				collection="relevancy-questionnaires"
				rows={loadedValue(relevancyQuestionnaires.value()) ?? []}
				label={(entry: RelevancyQuestionnaire) => personaName(entry.persona)}
				detailHeader={i18n.t().common.status}
				detail={(entry: RelevancyQuestionnaire) =>
					statusLabel(i18n.t(), relevancyStatus(entry))
				}
				onChanged={() => void relevancyQuestionnaires.reload()}
			/>
		</div>
	);

	const HistoryTab = () => (
		<Panel title={i18n.t().customers.history}>
			<Async value={history.value()} onRetry={history.reload}>
				{(events) => (
					<Table
						rows={events}
						rowKey={(event) => String(event.id)}
						emptyLabel={i18n.t().history.noEvents}
						columns={[
							{
								key: "at",
								header: i18n.t().history.at,
								cell: (event) => formatMoment(event.at, i18n.locale()),
							},
							{
								key: "entity",
								header: i18n.t().dashboard.entity,
								cell: (event) => `${event.entity_type} ${event.entity_id}`,
							},
							{
								key: "actor",
								header: i18n.t().history.actor,
								cell: (event) => event.actor,
							},
							{
								key: "change",
								header: i18n.t().history.change,
								cell: (event) => describeChange(i18n.t(), event),
							},
						]}
					/>
				)}
			</Async>
		</Panel>
	);

	const failure = () => {
		const state = customer.value();
		return state.state === "failed" ? state.error : undefined;
	};

	return (
		<Show
			when={!(failure() !== undefined && missing(failure() as ApiError))}
			fallback={
				<div class={styles.heading}>
					<h1>{i18n.t().customers.title}</h1>
					<p class={styles.problem}>{i18n.t().customers.notFound}</p>
					<A href="/customers">{i18n.t().customers.title}</A>
				</div>
			}
		>
			<Async value={customer.value()} onRetry={customer.reload}>
				{(own) => (
					<>
						<div class={styles.heading}>
							<h1>{own.name}</h1>
							<Show when={own.description}>
								{(description) => <p class={styles.lede}>{description()}</p>}
							</Show>
							<div class={styles.inline}>
								<Toggle
									label={i18n.t().common.active}
									checked={live(own).active}
									onToggle={(active) => void patch({ active })}
								/>
								<Button
									label={i18n.t().preview.enter}
									tone="primary"
									onClick={() => session.enterPreview(own.id)}
								/>
							</div>
							<Show when={problem() !== undefined}>
								<p class={styles.problem}>{problem()}</p>
							</Show>
						</div>

						<Tabs
							label={own.name}
							active={tab()}
							onPick={setTab}
							tabs={[
								{
									id: "users",
									label: i18n.t().customers.users,
									content: () => (
										<UsersPanel
											customer={own.id}
											title={i18n.t().customers.users}
										/>
									),
								},
								{
									id: "access",
									label: i18n.t().customers.access,
									content: () => <AccessTab customer={own} />,
								},
								{
									id: "history",
									label: i18n.t().customers.history,
									content: () => <HistoryTab />,
								},
							]}
						/>
					</>
				)}
			</Async>
		</Show>
	);
};

/** The customer's name for a tag, or the internal label. */
export const ownerLabel = (
	customers: readonly Customer[] | undefined,
	owner: string | null | undefined,
	internal: string,
): string =>
	owner
		? (customers?.find((entry) => entry.id === owner)?.name ?? owner)
		: internal;

/** A small owner tag: which customer an entry belongs to. */
export const OwnerTag = (props: {
	customers: readonly Customer[] | undefined;
	owner: string | null | undefined;
}) => {
	const i18n = useI18n();
	return (
		<span
			class={`${styles.ownerTag} ${props.owner ? "" : styles.ownerInternal}`}
		>
			{ownerLabel(props.customers, props.owner, i18n.t().common.internal)}
		</span>
	);
};

/** The customer select shared by the owned-entity forms. */
export const CustomerSelect = (props: {
	customers: readonly Customer[] | undefined;
	value: string | null | undefined;
	hint?: string;
	onPick: (customer: string | null) => void;
}) => {
	const i18n = useI18n();
	return (
		<SelectField
			label={i18n.t().common.customer}
			hint={props.hint ?? i18n.t().common.customerHint}
			optional
			placeholder={i18n.t().common.noCustomer}
			choices={(props.customers ?? []).map((customer) => ({
				id: customer.id,
				label: customer.name,
			}))}
			value={props.value ?? ""}
			onPick={(customer) => props.onPick(customer === "" ? null : customer)}
		/>
	);
};
